import styled from '@emotion/styled';
import {twMerge} from 'tailwind-merge';
import React, {FC, ReactNode} from 'react';
import {VerdocsEndpoint} from '@verdocs/js-sdk';
import {ChevronDownIcon} from '@heroicons/react/24/outline';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import {DocumentIcon, Bars3Icon, Cog6ToothIcon} from '@heroicons/react/24/solid';
import {EnvelopeOpenIcon, ArrowRightStartOnRectangleIcon} from '@heroicons/react/24/solid';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger} from '../../Components';
import {ProfileChooser} from '../../Components/ProfileChooser';
import {useDefineSettings} from '../../hooks/useDefineSettings';
import {PreferencesMenu} from './PreferencesMenu';
import {track} from '../../Managers/Analytics';
import {groupBy} from '../../utils';
import {URLS} from '../../Routing';

const StyledNavLink = styled(NavLink)`
  display: flex;
  color: #f2f5fa;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid transparent;
  white-space: nowrap;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;

  &.active {
    color: #33364b;
    border: 1px solid #ccc;
    background-color: #e3e7f7;
  }

  @media all and (max-width: 768px) {
    font-size: 14px;
    padding: 4px;
    margin: 0;
    flex: 0;
  }

  @media all and (max-width: 480px) {
    font-size: 13px;
    padding: 0 4px;
    margin: 0;
    flex: 0;
  }
`;

const StyledNavButton = styled.div`
  display: flex;
  color: #f2f5fa;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid transparent;
  white-space: nowrap;
  align-items: center;
  text-decoration: none;
  box-sizing: border-box;

  &.active {
    color: #33364b;
    border: 1px solid #ccc;
    background-color: #e3e7f7;
  }

  @media all and (max-width: 768px) {
    font-size: 14px;
    padding: 4px;
    margin: 0;
    flex: 0;
  }

  @media all and (max-width: 480px) {
    font-size: 13px;
    padding: 0 4px;
    margin: 0;
    flex: 0;
  }
`;

// const Separator = () => <div className="flex flex-none h-[1px] bg-[#ffffff3f]" />;
// profile {"id":"e2e41049-abb6-4ddd-b7b9-c1c37d2d3e4d","user_id":"auth0|6018196ed93ea100707a39a5","organization_id":"65b59646-64fe-48df-850f-05887ddf003b","first_name":"Chad","last_name":"Robinson","email":"crobinson@medialantern.com","phone":null,"current":true,"organization":{"id":"65b59646-64fe-48df-850f-05887ddf003b","name":"realster","business_name":null,"is_business":false}}

const SidebarLink: FC<{
  className?: string;
  label: string;
  to?: string;
  onClick?: () => void;
  icon: any;
  trailing?: ReactNode;
}> = ({className, label, to, onClick, icon, trailing}) => {
  const id = label.toLowerCase().replace(/ /g, '_');

  const handleClick = () => {
    track('nav_clicked', {item: id});
    onClick?.();
  };

  return to ? (
    <StyledNavLink to={to} id={`menu-item-${id}`} onClick={handleClick} className={className}>
      <span className={'mr-2 hidden phablet:inline'}>{icon}</span>
      <span>{label}</span>
      {trailing}
    </StyledNavLink>
  ) : (
    <StyledNavButton onClick={handleClick} id={`menu-item-${id}`} className={className}>
      <span className={'mr-2 hidden phablet:inline'}>{icon}</span>
      <span>{label}</span>
    </StyledNavButton>
  );
};

// The settings pages, nested under Settings while a settings route is open. Children are the
// one list the mobile settings dropdown also reads, so the two never drift.
const SettingsTree: FC = () => {
  const groups = groupBy(useDefineSettings(), 'group');

  return (
    <div className="mb-2 hidden tablet:block">
      {Object.entries(groups).map(([group, items]) => (
        <React.Fragment key={group}>
          <div className="pb-0.5 pl-8 pt-2.5 text-[10px] font-medium uppercase tracking-wider text-[#8c90a8]">{group}</div>
          {items.map((item) =>
            item.disabled ? (
              <div key={item.to} aria-disabled="true" className="block cursor-default whitespace-nowrap py-1 pl-8 pr-2 text-[12.5px] text-[#7d8197]">
                {item.label}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => track('nav_clicked', {item: item.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')})}
                className={({isActive}) =>
                  twMerge(
                    'block whitespace-nowrap rounded-md py-1 pl-8 pr-2 text-[12.5px] font-medium text-[#d7d9e4] no-underline transition-colors hover:bg-white/5 hover:text-white',
                    isActive && 'bg-white/10 font-semibold text-white',
                  )
                }>
                {item.label}
              </NavLink>
            ),
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const Sidebar: FC<{onSignout?: () => void}> = ({onSignout}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inSettings = location.pathname === URLS.SETTINGS_BASE.build() || location.pathname.startsWith(`${URLS.SETTINGS_BASE.build()}/`);

  // The mobile menu doesn't route through SidebarLink, so it tracks its own clicks.
  const handleNavigate = (item: string, to: string) => {
    track('nav_clicked', {item});
    navigate(to);
  };

  const handleSignout = async () => {
    // We try to send a signout, but proceed whether it fails or not
    const endpoint = VerdocsEndpoint.getDefault();
    await endpoint.api.delete('/v2/users/sessions/current', {timeout: 3000}).catch(() => {});

    endpoint.clearSession();
    const isBeta = window.location.host.includes('beta');
    const isDev = window.location.host.includes('localhost');
    if (!isBeta && !isDev) {
      window.location.replace('https://verdocs.com/');
    } else if (isBeta) {
      window.location.replace('https://www.stage.verdocs.com/');
    } else {
      window.location.reload();
    }
    onSignout?.();
  };

  // NOTE: "tablet" is the main breakpoint between desktop and mobile layouts
  return (
    <div
      style={{transition: 'all 0.3s'}}
      className={twMerge(
        'flex flex-0 flex-row text-gray-100 font-medium box-border bg-[#33364b] items-center py-2',
        'tablet:flex-col tablet:flex-[0_0_200px] tablet:pb-4 tablet:justify-between',
      )}>
      {/* Initial sidebar items */}
      <div className="tablet:w-full">
        <ProfileChooser />

        <div
          className={twMerge(
            //
            'flex-row content-center gap-1 w-full px-3 hidden',
            'tablet:flex tablet:flex-col',
          )}>
          <SidebarLink label="Dashboard" to={URLS.DASHBOARD.build()} icon={<Bars3Icon className="size-4 tablet:size-5" />} />
          <SidebarLink label="Templates" to={URLS.TEMPLATES.build()} icon={<DocumentIcon className="size-4 tablet:size-5" />} />
          <SidebarLink label="Envelopes" to={URLS.ENVELOPES.build()} icon={<EnvelopeOpenIcon className="size-4 tablet:size-5" />} />
          <SidebarLink
            label="Settings"
            to={URLS.SETTINGS_BASE.build()}
            icon={<Cog6ToothIcon className="size-4 tablet:size-5" />}
            trailing={<ChevronDownIcon className={twMerge('ml-auto hidden size-4 transition-transform tablet:block', !inSettings && '-rotate-90')} />}
          />
          {inSettings && <SettingsTree />}
        </div>
      </div>

      {/* Additional sidebar items */}
      <div className="items-center flex-col hidden phablet:flex mr-[10%] tablet:mr-0">
        <img
          src={'https://app.verdocs.com/assets/white-logo.svg'}
          alt="Verdocs logo"
          className="w-[100px] mr-4 tablet:mr-0 hidden tablet:block tablet:mb-6"
        />

        <div className="hidden tablet:flex justify-center flex-col gap-0.5 pb-2">
          <a
            href="https://verdocs.com/eula/"
            target="_blank"
            rel="noreferrer"
            className="block text-gray-300 no-underline text-center text-xs cursor-pointer my-1">
            End User License Agreement
          </a>
          <a
            href="https://verdocs.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
            className="block text-gray-300 no-underline text-center text-xs cursor-pointer my-1">
            Privacy Policy
          </a>

          <div className="my-2" />

          <PreferencesMenu>
            <SidebarLink label="Sign Out" onClick={handleSignout} icon={<ArrowRightStartOnRectangleIcon className="size-4 tablet:w-6 tablet:h-6" />} />
          </PreferencesMenu>
        </div>
      </div>

      {/* Mobile menu */}
      <div className="flex flex-1 tablet:hidden" />
      <div className="flex flex-0 tablet:hidden pr-2">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Bars3Icon className="size-6" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handleNavigate('dashboard', URLS.DASHBOARD.build())}>Dashboard</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNavigate('templates', URLS.TEMPLATES.build())}>Templates</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNavigate('envelopes', URLS.ENVELOPES.build())}>Envelopes</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNavigate('settings', URLS.SETTINGS_BASE.build())}>Settings</DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <a
                href="https://verdocs.com/eula/"
                target="_blank"
                rel="noreferrer"
                className="block text-gray-500 no-underline text-center text-xs cursor-pointer">
                End User License Agreement
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <a
                href="https://verdocs.com/privacy-policy/"
                target="_blank"
                rel="noreferrer"
                className="block text-gray-500 no-underline text-center text-xs cursor-pointer">
                Privacy Policy
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                track('nav_clicked', {item: 'sign_out'});
                handleSignout();
              }}>
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
